import { useQuery, useMutation } from '@apollo/client';
import { GET_HABITACIONES_CON_OCUPACION, REGISTRAR_LIMPIEZA } from '../../graphql/habitaciones';
import { CheckCircle, Wrench, AlertTriangle, BedDouble, Users } from 'lucide-react';
import './HousekeepingPanel.css';

const ESTADO_CONFIG = {
  disponible: { label: 'Limpia', className: 'hkp__status--disponible', icon: CheckCircle },
  ocupada: { label: 'Ocupada', className: 'hkp__status--ocupada', icon: BedDouble },
  limpieza: { label: 'Pendiente', className: 'hkp__status--limpieza', icon: AlertTriangle },
  mantenimiento: { label: 'Manto.', className: 'hkp__status--mantenimiento', icon: Wrench },
  reservada: { label: 'Reservada', className: 'hkp__status--reservada', icon: BedDouble },
};

function HousekeepingPanel() {
  const { data, loading, refetch } = useQuery(GET_HABITACIONES_CON_OCUPACION, {
    pollInterval: 10000,
  });

  const [registrarLimpieza] = useMutation(REGISTRAR_LIMPIEZA, {
    onCompleted: () => refetch(),
  });

  const habitaciones = (data?.habitacionesConOcupacion || []).filter(h => h.activa !== false);

  // Summary counts
  const stats = {
    total: habitaciones.length,
    disponible: habitaciones.filter(h => h.estado === 'disponible').length,
    ocupada: habitaciones.filter(h => h.estado === 'ocupada').length,
    limpieza: habitaciones.filter(h => h.estado === 'limpieza').length,
    mantenimiento: habitaciones.filter(h => h.estado === 'mantenimiento').length,
  };

  // Bed stats
  const totalCamas = habitaciones.reduce((sum, h) => sum + (h.capacidad || 0), 0);
  const camasOcupadas = habitaciones.reduce((sum, h) => sum + (h.camas_ocupadas || 0), 0);
  const camasLibres = totalCamas - camasOcupadas;
  const habsConCamasLibres = habitaciones.filter(h => h.estado === 'ocupada' && (h.camas_disponibles || 0) > 0).length;

  const handleMarcarLimpia = async (habId) => {
    try {
      await registrarLimpieza({ variables: { habitacionId: parseInt(habId) } });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const getCamasBarColor = (hab) => {
    const ocupadas = hab.camas_ocupadas || 0;
    const cap = hab.capacidad || 1;
    if (ocupadas === 0) return 'hkp__camas-bar--libre';
    if (ocupadas >= cap) return 'hkp__camas-bar--llena';
    return 'hkp__camas-bar--parcial';
  };

  const getInfoHabitacion = (hab) => {
    const ocupantes = hab.ocupantes_activos || [];

    if (hab.estado === 'ocupada' && ocupantes.length > 0) {
      return (
        <div className="hkp__ocupantes-compact">
          {ocupantes.map((oc, i) => (
            <span key={i} className="hkp__ocupante-tag">
              <BedDouble size={10} />
              #{oc.cama_numero} {oc.nombre?.split(' ')[0]}
              {oc.grado_nombre && <span className="hkp__ocupante-grado">{oc.grado_nombre}</span>}
            </span>
          ))}
          {(hab.camas_disponibles || 0) > 0 && (
            <span className="hkp__camas-libres-tag">
              +{hab.camas_disponibles} libre{hab.camas_disponibles > 1 ? 's' : ''}
            </span>
          )}
        </div>
      );
    }

    if (hab.estado === 'limpieza') return <span>Pendiente de limpieza</span>;
    if (hab.estado === 'mantenimiento') return <span>En mantenimiento</span>;
    if (hab.estado === 'reservada') return <span>Reservada</span>;
    return <span className="hkp__info--sin">Sin ocupacion — {hab.capacidad} camas disponibles</span>;
  };

  return (
    <div className="hkp">
      <div className="hkp__header">
        <h2 className="hkp__title">
          Estado de habitaciones para hoy
          <span className="hkp__badge">{stats.total}</span>
        </h2>
      </div>

      {/* Summary - Habitaciones */}
      <div className="hkp__summary">
        <span className="hkp__stat hkp__stat--disponible">{stats.disponible} Disponibles</span>
        <span className="hkp__stat hkp__stat--ocupada">{stats.ocupada} Ocupadas</span>
        <span className="hkp__stat hkp__stat--limpieza">{stats.limpieza} Limpieza</span>
        <span className="hkp__stat hkp__stat--mantenimiento">{stats.mantenimiento} Manto.</span>
      </div>

      {/* Summary - Camas */}
      <div className="hkp__camas-summary">
        <Users size={14} />
        <span className="hkp__camas-summary-text">
          <strong>{camasOcupadas}</strong>/{totalCamas} camas ocupadas
        </span>
        <span className="hkp__camas-summary-sep">|</span>
        <span className="hkp__camas-summary-libres">{camasLibres} libres</span>
        {habsConCamasLibres > 0 && (
          <>
            <span className="hkp__camas-summary-sep">|</span>
            <span className="hkp__camas-summary-parcial">{habsConCamasLibres} hab. con camas libres</span>
          </>
        )}
      </div>

      {loading ? (
        <div className="hkp__loading">Cargando habitaciones...</div>
      ) : (
        <div className="hkp__tabla-wrapper">
          <table className="hkp__tabla">
            <thead>
              <tr>
                <th>Habitacion</th>
                <th>Camas</th>
                <th>Informacion</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {habitaciones
                .sort((a, b) => {
                  const numA = parseInt(a.numero) || 0;
                  const numB = parseInt(b.numero) || 0;
                  return numA - numB;
                })
                .map(hab => {
                  const ocupadas = hab.camas_ocupadas || 0;
                  const cap = hab.capacidad || 1;
                  const pct = Math.round((ocupadas / cap) * 100);

                  return (
                    <tr key={hab.id} className="hkp__fila">
                      <td>
                        <span className="hkp__hab-tipo">{hab.tipo}</span>
                        <span className="hkp__hab-sep"> | </span>
                        <span className="hkp__hab-num">{hab.numero}</span>
                      </td>
                      <td>
                        <div className="hkp__camas-cell">
                          <div className={`hkp__camas-bar ${getCamasBarColor(hab)}`}>
                            <div className="hkp__camas-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="hkp__camas-text">{ocupadas}/{cap}</span>
                        </div>
                      </td>
                      <td>
                        <div className="hkp__info">
                          {getInfoHabitacion(hab)}
                        </div>
                      </td>
                      <td>
                        {hab.estado === 'limpieza' ? (
                          <input
                            type="checkbox"
                            className="hkp__checkbox"
                            onChange={() => handleMarcarLimpia(hab.id)}
                            title="Marcar como limpia"
                          />
                        ) : hab.estado === 'disponible' ? (
                          <CheckCircle size={18} className="hkp__icon--clean" />
                        ) : hab.estado === 'mantenimiento' ? (
                          <Wrench size={18} className="hkp__icon--manto" />
                        ) : (
                          <span className={`hkp__estado-badge hkp__estado-badge--${hab.estado}`}>
                            {ESTADO_CONFIG[hab.estado]?.label || hab.estado}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default HousekeepingPanel;
